import { useState, useEffect, useCallback } from "react";
import {
  Plus, X, Trash2, Edit3, Image as ImageIcon, Link2, Code2, Sparkles,
  Play, Pause, BarChart3, AlertTriangle, Loader2, Monitor, Tablet, Smartphone, Upload,
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";

const STATUS_STYLES = {
  draft: "bg-slate-700/40 text-slate-300", scheduled: "bg-blue-500/15 text-blue-400",
  active: "bg-green-500/15 text-green-400", paused: "bg-amber-500/15 text-amber-400",
  expired: "bg-slate-700/40 text-slate-500", archived: "bg-slate-700/40 text-slate-500",
};
const TYPE_ICONS = { image: ImageIcon, url: Link2, html: Code2, ai: Sparkles };
const emptyAd = {
  name: "", type: "image", placement: "homepage_middle",
  image_desktop: "", image_tablet: "", image_mobile: "", image_url: "", html_code: "",
  title_en: "", title_fr: "", cta_label_en: "", cta_label_fr: "", cta_url: "",
  alt_text_en: "", alt_text_fr: "",
  audience: { users: "all", show_to_premium: false, countries: [], languages: [], devices: [] },
  start_date: "", end_date: "", status: "draft",
  impression_limit: "", click_limit: "", open_new_tab: true,
};

export default function AdminAds() {
  const [ads, setAds] = useState([]);
  const [counts, setCounts] = useState({});
  const [placements, setPlacements] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(emptyAd);
  const [busy, setBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiDevice, setAiDevice] = useState("desktop");
  const [aiBusy, setAiBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const [stats, setStats] = useState(null);

  const uploadImage = async (deviceKey, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { alert("Image too large (max 3MB)"); return; }
    setUploading(deviceKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/media/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      set(deviceKey, r.data.asset.url);
    } catch (e) { alert(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(""); }
  };

  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ads`, { params: { status: statusFilter }, withCredentials: true });
      setAds(r.data.ads); setCounts(r.data.counts);
    } catch { }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchAds(); }, [fetchAds]);
  useEffect(() => {
    axios.get(`${API}/admin/ads/placements`, { withCredentials: true }).then(r => setPlacements(r.data.placements)).catch(() => {});
  }, []);

  const openCreate = () => { setForm(emptyAd); setAiPrompt(""); setEditor({ mode: "create" }); };
  const openEdit = (ad) => {
    setForm({ ...emptyAd, ...ad, audience: { ...emptyAd.audience, ...(ad.audience || {}) },
      impression_limit: ad.impression_limit || "", click_limit: ad.click_limit || "",
      start_date: ad.start_date ? ad.start_date.slice(0, 16) : "", end_date: ad.end_date ? ad.end_date.slice(0, 16) : "" });
    setEditor({ mode: "edit", id: ad.id });
  };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAud = (k, v) => setForm(f => ({ ...f, audience: { ...f.audience, [k]: v } }));

  const save = async () => {
    if (!form.name.trim()) { alert("Ad name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null };
      if (editor.mode === "create") await axios.post(`${API}/admin/ads`, payload, { withCredentials: true });
      else await axios.put(`${API}/admin/ads/${editor.id}`, payload, { withCredentials: true });
      setEditor(null); fetchAds();
    } catch (e) { alert(e.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  const genImage = async () => {
    if (!aiPrompt.trim()) { alert("Enter a prompt"); return; }
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/admin/ads/generate-image`,
        { placement: form.placement, device: aiDevice, prompt: aiPrompt }, { withCredentials: true });
      const key = aiDevice === "mobile" ? "image_mobile" : aiDevice === "tablet" ? "image_tablet" : "image_desktop";
      set(key, r.data.url);
    } catch (e) { alert(e.response?.data?.detail || "Generation failed"); }
    finally { setAiBusy(false); }
  };

  const setStatus = async (ad, status) => {
    await axios.post(`${API}/admin/ads/${ad.id}/status`, { status }, { withCredentials: true }); fetchAds();
  };
  const del = async (ad) => {
    if (!window.confirm(`Delete "${ad.name}"?`)) return;
    await axios.delete(`${API}/admin/ads/${ad.id}`, { withCredentials: true }); fetchAds();
  };
  const openStats = async (ad) => {
    const r = await axios.get(`${API}/admin/ads/${ad.id}/stats`, { withCredentials: true });
    setStats({ ...r.data, name: ad.name });
  };

  const prof = placements[form.placement];
  const tabs = [["", "All"], ["active", "Active"], ["scheduled", "Scheduled"], ["paused", "Paused"], ["draft", "Draft"], ["expired", "Expired"]];

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-slate-400">Manage promotional ads across the site with targeting, scheduling and reporting.</p>
        <button onClick={openCreate} data-testid="new-ad-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold">
          <Plus size={16} /> New Ad
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mb-4" data-testid="ad-status-tabs">
        {tabs.map(([v, l]) => (
          <button key={v} onClick={() => setStatusFilter(v)} data-testid={`ad-tab-${v || "all"}`}
            className={cn("px-3 py-1.5 rounded-lg text-xs font-medium", statusFilter === v ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white")}>
            {l}{v && counts[v] != null ? ` (${counts[v]})` : ""}
          </button>
        ))}
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden" data-testid="ads-table">
        {loading ? <div className="p-10 text-center text-slate-500"><Loader2 className="animate-spin mx-auto" /></div>
          : ads.length === 0 ? <div className="p-12 text-center text-slate-400" data-testid="ads-empty">No ads yet.</div>
          : <div className="divide-y divide-slate-800/60">
            {ads.map(ad => {
              const Icon = TYPE_ICONS[ad.type] || ImageIcon;
              return (
                <div key={ad.id} className="p-4 flex items-center gap-4 hover:bg-slate-800/20" data-testid={`ad-row-${ad.id}`}>
                  <div className="p-2.5 rounded-lg bg-slate-800"><Icon size={16} className="text-blue-400" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white truncate">{ad.name}</span>
                      <span className={cn("px-2 py-0.5 rounded-md text-[11px] font-medium capitalize", STATUS_STYLES[ad.effective_status])} data-testid={`ad-status-${ad.id}`}>{ad.effective_status}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{ad.placement} · {ad.impressions || 0} impressions · {ad.clicks || 0} clicks · CTR {ad.ctr || 0}%</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => openStats(ad)} data-testid={`ad-stats-${ad.id}`} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"><BarChart3 size={15} /></button>
                    {ad.effective_status === "active"
                      ? <button onClick={() => setStatus(ad, "paused")} data-testid={`ad-pause-${ad.id}`} className="p-2 rounded-lg bg-amber-500/10 text-amber-400"><Pause size={15} /></button>
                      : <button onClick={() => setStatus(ad, "active")} data-testid={`ad-activate-${ad.id}`} className="p-2 rounded-lg bg-green-500/10 text-green-400"><Play size={15} /></button>}
                    <button onClick={() => openEdit(ad)} data-testid={`ad-edit-${ad.id}`} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"><Edit3 size={15} /></button>
                    <button onClick={() => del(ad)} data-testid={`ad-delete-${ad.id}`} className="p-2 rounded-lg bg-red-500/10 text-red-400"><Trash2 size={15} /></button>
                  </div>
                </div>
              );
            })}
          </div>}
      </div>

      {editor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="ad-editor">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
              <h3 className="text-base font-semibold text-white">{editor.mode === "create" ? "New Ad" : "Edit Ad"}</h3>
              <button onClick={() => setEditor(null)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-4 text-sm">
              <input value={form.name} onChange={e => set("name", e.target.value)} data-testid="ad-name" placeholder="Ad name" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Type</label>
                  <select value={form.type} onChange={e => set("type", e.target.value)} data-testid="ad-type" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200">
                    <option value="image">Upload / Image</option><option value="url">Image URL</option><option value="html">HTML (Super Admin)</option><option value="ai">AI Generated</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Placement</label>
                  <select value={form.placement} onChange={e => set("placement", e.target.value)} data-testid="ad-placement" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200">
                    {Object.keys(placements).map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              {prof && (
                <div className="rounded-lg bg-slate-950 border border-slate-800 p-3 text-xs text-slate-400" data-testid="size-guide">
                  <div className="text-white font-medium mb-1.5">{prof.label} — recommended sizes ({prof.aspect})</div>
                  <div className="flex gap-4 flex-wrap">
                    <span className="flex items-center gap-1"><Monitor size={12} /> {prof.desktop[0]}×{prof.desktop[1]}</span>
                    <span className="flex items-center gap-1"><Tablet size={12} /> {prof.tablet[0]}×{prof.tablet[1]}</span>
                    <span className="flex items-center gap-1"><Smartphone size={12} /> {prof.mobile[0]}×{prof.mobile[1]}</span>
                    <span>Max {prof.max_file_mb}MB</span>
                  </div>
                </div>
              )}

              {form.type === "html" ? (
                <div>
                  <label className="text-xs text-amber-400 block mb-1 flex items-center gap-1"><AlertTriangle size={12} /> HTML is sanitized (scripts, event handlers & javascript: URLs are stripped) and rendered in a sandboxed iframe.</label>
                  <textarea rows={5} value={form.html_code} onChange={e => set("html_code", e.target.value)} data-testid="ad-html" placeholder="<div>...</div>" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono text-xs" />
                </div>
              ) : form.type === "url" ? (
                <input value={form.image_url} onChange={e => set("image_url", e.target.value)} data-testid="ad-image-url" placeholder="https://.../banner.jpg" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white" />
              ) : form.type === "ai" ? (
                <div className="space-y-2 rounded-lg bg-slate-950 border border-slate-800 p-3">
                  <div className="flex gap-2">
                    {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, I]) => (
                      <button key={d} onClick={() => setAiDevice(d)} data-testid={`ai-device-${d}`} className={cn("px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 border", aiDevice === d ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400")}><I size={12} /> {d}</button>
                    ))}
                  </div>
                  <textarea rows={2} value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} data-testid="ai-prompt" placeholder="e.g. Advertisement for a Congolese music event" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs" />
                  <button onClick={genImage} disabled={aiBusy} data-testid="ai-generate" className="w-full py-2 rounded-lg bg-purple-600 text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50">
                    {aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate {aiDevice} image
                  </button>
                  {form[aiDevice === "mobile" ? "image_mobile" : aiDevice === "tablet" ? "image_tablet" : "image_desktop"] && (
                    <img alt="preview" src={form[aiDevice === "mobile" ? "image_mobile" : aiDevice === "tablet" ? "image_tablet" : "image_desktop"]} className="w-full rounded-lg" />
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {[["image_desktop", "Desktop", Monitor], ["image_tablet", "Tablet (optional)", Tablet], ["image_mobile", "Mobile (optional)", Smartphone]].map(([k, label, I]) => (
                    <div key={k} className="rounded-lg bg-slate-950 border border-slate-800 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-slate-400 flex items-center gap-1.5"><I size={13} /> {label}</span>
                        <label className={cn("px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer flex items-center gap-1.5", uploading === k ? "bg-slate-700 text-slate-400" : "bg-blue-600 text-white hover:bg-blue-500")} data-testid={`ad-upload-${k}`}>
                          {uploading === k ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {uploading === k ? "Uploading…" : "Upload image"}
                          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={uploading === k}
                            onChange={e => { uploadImage(k, e.target.files?.[0]); e.target.value = ""; }} />
                        </label>
                      </div>
                      {form[k] && <img alt={label} src={form[k]} className="w-full max-h-40 object-contain rounded-lg mb-2 bg-black/30" data-testid={`ad-preview-${k}`} />}
                      <input value={form[k]} onChange={e => set(k, e.target.value)} data-testid={`ad-${k}`} placeholder="…or paste an image URL" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs" />
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <input value={form.title_en} onChange={e => set("title_en", e.target.value)} data-testid="ad-title-en" placeholder="Title (EN)" className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs" />
                <input value={form.title_fr} onChange={e => set("title_fr", e.target.value)} data-testid="ad-title-fr" placeholder="Titre (FR)" className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs" />
                <input value={form.cta_label_en} onChange={e => set("cta_label_en", e.target.value)} placeholder="CTA label (EN)" className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs" />
                <input value={form.cta_label_fr} onChange={e => set("cta_label_fr", e.target.value)} placeholder="CTA label (FR)" className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs" />
              </div>
              <input value={form.cta_url} onChange={e => set("cta_url", e.target.value)} data-testid="ad-cta-url" placeholder="Click-through URL (https://...)" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs" />

              <div className="rounded-lg bg-slate-950 border border-slate-800 p-3 space-y-2">
                <label className="text-xs text-slate-500 block">Audience</label>
                <select value={form.audience.users} onChange={e => setAud("users", e.target.value)} data-testid="ad-audience-users" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 text-xs">
                  {[["all","All users"],["free","Free users only"],["premium","Premium users"],["authenticated","Signed-in users"],["guests","Guests"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <input type="checkbox" checked={!!form.audience.show_to_premium} onChange={e => setAud("show_to_premium", e.target.checked)} data-testid="ad-show-premium" className="rounded border-slate-600" />
                  Show this ad to Premium users too
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500 block mb-1">Start date</label><input type="datetime-local" value={form.start_date} onChange={e => set("start_date", e.target.value)} data-testid="ad-start" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 text-xs" /></div>
                <div><label className="text-xs text-slate-500 block mb-1">End date</label><input type="datetime-local" value={form.end_date} onChange={e => set("end_date", e.target.value)} data-testid="ad-end" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 text-xs" /></div>
                <div><label className="text-xs text-slate-500 block mb-1">Impression limit</label><input type="number" value={form.impression_limit} onChange={e => set("impression_limit", e.target.value)} placeholder="∞" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 text-xs" /></div>
                <div><label className="text-xs text-slate-500 block mb-1">Click limit</label><input type="number" value={form.click_limit} onChange={e => set("click_limit", e.target.value)} placeholder="∞" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 text-xs" /></div>
              </div>

              <div className="flex items-center justify-between">
                <select value={form.status} onChange={e => set("status", e.target.value)} data-testid="ad-status-select" className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 text-xs">
                  {["draft","active","paused","archived"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <label className="flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={form.open_new_tab} onChange={e => set("open_new_tab", e.target.checked)} className="rounded border-slate-600" /> Open in new tab</label>
              </div>

              <div className="flex gap-2 pt-1">
                <button onClick={() => setEditor(null)} className="flex-1 px-4 py-2.5 rounded-lg bg-slate-800 text-slate-300 text-sm">Cancel</button>
                <button onClick={save} disabled={busy} data-testid="ad-save" className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-bold text-sm disabled:opacity-50">{busy ? "Saving…" : "Save Ad"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {stats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="ad-stats-modal" onClick={() => setStats(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="text-base font-semibold text-white">{stats.name} — Report</h3><button onClick={() => setStats(null)} className="p-1.5 text-slate-400"><X size={18} /></button></div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[["Impressions", stats.impressions], ["Clicks", stats.clicks], ["CTR", `${stats.ctr}%`]].map(([l, v]) => (
                <div key={l} className="bg-slate-950 rounded-xl p-3 border border-slate-800"><div className="text-xl font-bold text-white">{v}</div><div className="text-[11px] text-slate-500">{l}</div></div>
              ))}
            </div>
            <div className="text-xs text-slate-400"><div className="text-white font-medium mb-1">By device</div>
              {Object.entries(stats.by_device || {}).map(([k, v]) => <div key={k}>{k}: {v.impressions} imp · {v.clicks} clicks</div>)}
              {Object.keys(stats.by_device || {}).length === 0 && <span>No data yet.</span>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
